/**
 * tenantRouterProvider — vibe-designer 1.98.0 (MP3.b)
 *
 * Fetches /api/chat-proxy/v1/models and transforms usable entries into
 * pi-mono Model<openai-completions> objects packaged as a pi-web-ui
 * CustomProvider. Persisted via AppStorage.customProviders so pi's
 * <agent-model-selector> dialog sources its model list from the user's
 * tenant LLM router instead of pi-ai's hardcoded Amazon Bedrock /
 * Anthropic / OpenAI / Google catalog.
 *
 * Verified file-anchors (do not change without updating piAgentFactory):
 *   - TENANT_ROUTER_PROVIDER === piAgentFactory.ts:154 provider string
 *   - compat fields === piAgentFactory.ts:178-181
 *   - cost shape === piAgentFactory.ts:183
 *
 * Filter rules (per worldview §11 Q4 STRICT):
 *   - keep `has_api_key === true` (drops cloud catalog the tenant has no key for)
 *   - drop `status === "unhealthy"` (keep healthy + available + others)
 *   - preserve `id: "auto"` (the router-decides synthetic entry)
 *
 * Fallback (per worldview §11 Q5 once-per-page-load):
 *   - on fetch failure: log + return empty array; caller (ChatPanelPi)
 *     skips customProviders.set and onModelSelect passes
 *     allowedProviders=undefined → pi's built-in catalog stays visible.
 *
 * G0 reviewer trail (2026-05-11):
 *   - [B5] TENANT_ROUTER_PROVIDER string equality verified
 *   - [B3] CustomProvider shape verified at custom-providers-store.ts:13-23
 *   - [B4] /models response shape verified against live tenant capture
 *   - [C2] Idempotent via TENANT_ROUTER_PROVIDER_ID IndexedDB key overwrite
 */
import type { CustomProvider } from "@earendil-works/pi-web-ui";
import type { Model } from "@earendil-works/pi-ai";

export const TENANT_ROUTER_PROVIDER = "gamemagick-router" as const;

export const TENANT_ROUTER_PROVIDER_ID = "tenant-llm-router" as const;

/** Shape of one entry in /api/chat-proxy/v1/models response.data[].
 *  Mirrors the tenant LLM router's /v1/models output (mirrored by chat-proxy
 *  per chat_proxy.py:28-38). Fields beyond what we consume are ignored. */
export interface TenantRouterModelEntry {
  id: string;
  object?: string;
  name?: string;
  owned_by?: string;
  type?: string;
  capabilities?: string[];
  status?: string;
  has_api_key?: boolean;
  context_length?: number;
  max_output_tokens?: number;
  max_input_tokens?: number;
  max_model_len?: number;
}

interface TenantRouterModelsResponse {
  data?: TenantRouterModelEntry[];
}

const DEFAULT_CONTEXT_WINDOW = 262_144;

function isUsable(entry: TenantRouterModelEntry): boolean {
  if (entry.has_api_key !== true) return false;
  if (entry.status === "unhealthy") return false;
  return true;
}

function entryReasoning(entry: TenantRouterModelEntry): boolean {
  const caps = entry.capabilities ?? [];
  return caps.includes("reasoning") || caps.includes("thinking");
}

// Whether the router advertises this model as image-capable. pi-agent's
// openai-completions provider only sends image content (pasted images, browser
// screenshots) when model.input includes "image" — otherwise it strips them. So
// a vision model MUST carry input:["text","image"] here or the picture never
// reaches it. `image`/`multimodal` accepted as aliases for forward-compat.
function entryVision(entry: TenantRouterModelEntry): boolean {
  const caps = entry.capabilities ?? [];
  return caps.includes("vision") || caps.includes("image") || caps.includes("multimodal");
}

function entryContextWindow(entry: TenantRouterModelEntry): number {
  if (entry.max_input_tokens && entry.max_input_tokens > 0) return entry.max_input_tokens;
  if (entry.max_model_len && entry.max_model_len > 0) return entry.max_model_len;
  return DEFAULT_CONTEXT_WINDOW;
}

function entryMaxTokens(entry: TenantRouterModelEntry): number {
  if (entry.max_output_tokens && entry.max_output_tokens > 0) return entry.max_output_tokens;
  return DEFAULT_CONTEXT_WINDOW;
}

function transformEntry(
  entry: TenantRouterModelEntry,
  absoluteBaseUrl: string,
): Model<"openai-completions"> {
  const isAuto = entry.id === "auto";
  return {
    id: entry.id,
    name: entry.name ?? entry.id,
    api: "openai-completions",
    provider: TENANT_ROUTER_PROVIDER,
    baseUrl: absoluteBaseUrl,
    reasoning: isAuto ? true : entryReasoning(entry),
    compat: {
      thinkingFormat: "qwen-chat-template",
      supportsDeveloperRole: false,
    },
    input: entryVision(entry) ? ["text", "image"] : ["text"],
    cost: { input: 0, output: 0, cacheRead: 0, cacheWrite: 0 },
    contextWindow: isAuto ? DEFAULT_CONTEXT_WINDOW : entryContextWindow(entry),
    maxTokens: isAuto ? DEFAULT_CONTEXT_WINDOW : entryMaxTokens(entry),
  };
}

/** Fetch /models and transform usable entries into Model<openai-completions>.
 *  Returns [] on ANY failure (caller decides fallback behavior). */
export async function fetchTenantRouterModels(
  baseUrl: string,
  authToken: string,
): Promise<Model<"openai-completions">[]> {
  // 1.99.1: delegate to the with-raw helper to avoid duplication. The
  // public API for MP3.b consumers (ChatPanelPi.initPiAppStorage) stays
  // unchanged — they get Model<>[] only.
  const { models } = await fetchTenantRouterModelsWithRaw(baseUrl, authToken);
  return models;
}

/**
 * Like fetchTenantRouterModels, but also returns a Map<modelId, rawEntry>
 * so callers (piAgentFactory) can preserve filtering by capability fields
 * that the Model<openai-completions> shape doesn't carry. Used by
 * piAgentFactory v1.99.1 to pick a tool-capable default model.
 *
 * G0 [B3] (1.99.1): Map shape (vs parallel arrays in plan v1) ensures
 * index-drift safety. piAgentFactory does `rawById.get(model.id)` — no
 * positional assumption between two arrays.
 *
 * Both `models` and `rawById` are derived from the SAME isUsable filter
 * pass — entries excluded from `models` are also excluded from `rawById`.
 * Callers can iterate `models` and look up rawById entries by id with
 * guaranteed hit (for usable entries) or miss (otherwise — defensive
 * `?.` chain at the callsite).
 */
export async function fetchTenantRouterModelsWithRaw(
  baseUrl: string,
  authToken: string,
): Promise<{
  models: Model<"openai-completions">[];
  rawById: Map<string, TenantRouterModelEntry>;
}> {
  if (!authToken) return { models: [], rawById: new Map() };
  const absoluteBaseUrl = baseUrl.startsWith("http")
    ? baseUrl
    : `${window.location.origin}${baseUrl}`;
  try {
    const resp = await fetch(`${absoluteBaseUrl}/models`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!resp.ok) {
      console.warn(
        `[tenantRouterProvider] /models returned ${resp.status} ${resp.statusText}`,
      );
      return { models: [], rawById: new Map() };
    }
    const body = (await resp.json()) as TenantRouterModelsResponse;
    const allEntries = Array.isArray(body.data) ? body.data : [];
    const usableEntries = allEntries.filter(isUsable);
    const models = usableEntries.map((entry) => transformEntry(entry, absoluteBaseUrl));
    const rawById = new Map<string, TenantRouterModelEntry>(
      usableEntries.map((entry) => [entry.id, entry] as const),
    );
    return { models, rawById };
  } catch (err) {
    console.warn("[tenantRouterProvider] /models fetch failed:", err);
    return { models: [], rawById: new Map() };
  }
}

/** Build the CustomProvider object for IndexedDB write. Caller invokes
 *  AppStorage.customProviders.set(provider). */
export function buildTenantRouterCustomProvider(
  models: Model<"openai-completions">[],
  absoluteBaseUrl: string,
): CustomProvider {
  return {
    id: TENANT_ROUTER_PROVIDER_ID,
    name: TENANT_ROUTER_PROVIDER,
    type: "openai-completions",
    baseUrl: absoluteBaseUrl,
    models: models as Model<"openai-completions">[],
  };
}
